import React, { useRef, useState } from "react";
import { useTeamChat, getMessageStatusLabel } from "../context/TeamChatContext";
import { useFloatingWidget } from "../hooks/useFloatingWidget";
import "../styles/team-chat-widget.css";

function formatDateLabel(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}

function MessageStatus({ status, isOwn }) {
    if (!isOwn || !status) return null;
    const label = getMessageStatusLabel(status);

    return (
        <span className={`team-chat-msg-status team-chat-msg-status--${status}`} title={label}>
            {status === "sending" && <i className="bi bi-clock"></i>}
            {status === "sent" && <i className="bi bi-check2"></i>}
            {status === "delivered" && (
                <span className="team-chat-double-check">
                    <i className="bi bi-check2"></i>
                    <i className="bi bi-check2"></i>
                </span>
            )}
            {status === "seen" && (
                <span className="team-chat-double-check team-chat-double-check--seen">
                    <i className="bi bi-check2"></i>
                    <i className="bi bi-check2"></i>
                </span>
            )}
            {status === "failed" && <i className="bi bi-exclamation-circle"></i>}
            <span className="team-chat-msg-status-text">{label}</span>
        </span>
    );
}

function GroupModal({ onClose, onCreate, users, onlineUsers, getInitials }) {
    const [name, setName] = useState("");
    const [selected, setSelected] = useState([]);

    const toggle = (userName) => {
        setSelected((prev) =>
            prev.includes(userName) ? prev.filter((u) => u !== userName) : [...prev, userName]
        );
    };

    return (
        <div className="team-chat-modal-overlay" onClick={onClose}>
            <div className="team-chat-modal" onClick={(e) => e.stopPropagation()}>
                <div className="team-chat-modal-header">
                    <h5>Create Group</h5>
                    <button type="button" className="team-chat-icon-btn" onClick={onClose}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="team-chat-modal-body">
                    <label>Group name</label>
                    <input
                        className="team-chat-input-field"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Sales Team"
                    />
                    <label className="mt-3">Add members</label>
                    <div className="team-chat-user-pick-list">
                        {users.map((u) => (
                            <label key={u.Id} className="team-chat-user-pick">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(u.Name)}
                                    onChange={() => toggle(u.Name)}
                                />
                                <span className="team-chat-avatar team-chat-avatar--sm">
                                    {getInitials(u.Name)}
                                </span>
                                <span>{u.Name}</span>
                                {onlineUsers.includes(u.Name) && (
                                    <span className="team-chat-online-dot" title="Online"></span>
                                )}
                            </label>
                        ))}
                    </div>
                </div>
                <div className="team-chat-modal-footer">
                    <button type="button" className="team-chat-btn team-chat-btn--ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="team-chat-btn team-chat-btn--primary"
                        disabled={!name.trim() || !selected.length}
                        onClick={() => onCreate(name.trim(), selected)}
                    >
                        Create Group
                    </button>
                </div>
            </div>
        </div>
    );
}

function MessageBubble({ msg, isOwn, parseMentions, getMessageStatus, showSender }) {
    const time = msg.Created_At
        ? new Date(msg.Created_At).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : "";
    const status = isOwn ? getMessageStatus(msg) : null;

    if (msg.Message_Type === "system") {
        return <div className="team-chat-system-msg">{msg.Body}</div>;
    }

    return (
        <div className={`team-chat-msg ${isOwn ? "team-chat-msg--own" : ""} ${msg._status === "failed" ? "team-chat-msg--failed" : ""}`}>
            {!isOwn && showSender && (
                <div className="team-chat-msg-avatar">{msg.Sender_Name?.substring(0, 2).toUpperCase()}</div>
            )}
            <div className="team-chat-msg-content">
                {!isOwn && showSender && <div className="team-chat-msg-sender">{msg.Sender_Name}</div>}
                <div className="team-chat-msg-bubble">
                    {msg.Message_Type === "file" && msg.File_Url && (
                        <a
                            href={`${process.env.REACT_APP_BASE_URL}${msg.File_Url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="team-chat-file-link"
                        >
                            <span className="team-chat-file-icon">
                                <i className="bi bi-file-earmark-arrow-down"></i>
                            </span>
                            <span>
                                <strong>{msg.File_Name || "Download file"}</strong>
                                <small>Tap to download</small>
                            </span>
                        </a>
                    )}
                    {msg.Message_Type === "voice" && msg.File_Url && (
                        <div className="team-chat-voice-wrap">
                            <i className="bi bi-mic-fill"></i>
                            <audio controls className="team-chat-voice-player" preload="metadata">
                                <source src={`${process.env.REACT_APP_BASE_URL}${msg.File_Url}`} />
                            </audio>
                        </div>
                    )}
                    {msg.Body && msg.Message_Type !== "voice" && (
                        <div className="team-chat-msg-text">{parseMentions(msg.Body)}</div>
                    )}
                    <div className="team-chat-msg-meta">
                        <span className="team-chat-msg-time">{time}</span>
                        <MessageStatus status={status} isOwn={isOwn} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function groupMessagesByDate(messages) {
    const groups = [];
    let lastDate = null;
    messages.forEach((msg) => {
        const dateKey = msg.Created_At ? new Date(msg.Created_At).toDateString() : "unknown";
        if (dateKey !== lastDate) {
            groups.push({ type: "date", key: dateKey, label: formatDateLabel(msg.Created_At) });
            lastDate = dateKey;
        }
        groups.push({ type: "msg", key: msg.Id || msg.Client_Temp_Id, msg });
    });
    return groups;
}

export default function TeamChatWidget() {
    const {
        open,
        setOpen,
        view,
        setView,
        tab,
        setTab,
        conversations,
        activeConversation,
        setActiveConversation,
        messages,
        allUsers,
        onlineUsers,
        typingUsers,
        totalUnread,
        loadingMessages,
        showGroupModal,
        setShowGroupModal,
        incomingCall,
        activeCall,
        startDirectChat,
        createGroup,
        sendMessage,
        uploadFile,
        handleInputChange,
        startCall,
        acceptCall,
        rejectCall,
        hangUp,
        getInitials,
        parseMentions,
        getMessageStatus,
        username,
        messagesEndRef,
        recentNotif,
        formatPreview,
    } = useTeamChat();

    const { rootStyle, dragHandleProps, minimized, minimize, restore } = useFloatingWidget("team-chat");

    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [recording, setRecording] = useState(false);
    const [showMentionList, setShowMentionList] = useState(false);
    const [mentionFilter, setMentionFilter] = useState("");
    const fileRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const voiceChunksRef = useRef([]);

    const mentionCandidates = (activeConversation?.members || [])
        .filter((m) => m !== username && m.toLowerCase().includes(mentionFilter.toLowerCase()));

    const openChat = (conv) => {
        setActiveConversation(conv);
        setView("chat");
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text || sending) return;
        setSending(true);
        setInput("");
        await sendMessage(text);
        setSending(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const onInputChange = (e) => {
        const val = e.target.value;
        handleInputChange(val, setInput);
        const atMatch = val.match(/@(\w*)$/);
        if (atMatch) {
            setShowMentionList(true);
            setMentionFilter(atMatch[1] || "");
        } else {
            setShowMentionList(false);
        }
    };

    const insertMention = (name) => {
        const newVal = input.replace(/@\w*$/, `@${name} `);
        setInput(newVal);
        setShowMentionList(false);
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSending(true);
        try {
            const uploaded = await uploadFile(file);
            await sendMessage(file.name, "file", uploaded.url, uploaded.fileName);
        } catch {
            /* toast in context */
        }
        setSending(false);
        e.target.value = "";
    };

    const startVoiceRecord = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            voiceChunksRef.current = [];
            recorder.ondataavailable = (ev) => voiceChunksRef.current.push(ev.data);
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(voiceChunksRef.current, { type: "audio/webm" });
                const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
                setSending(true);
                try {
                    const uploaded = await uploadFile(file);
                    await sendMessage("Voice message", "voice", uploaded.url, uploaded.fileName);
                } catch {
                    /* ignore */
                }
                setSending(false);
            };
            mediaRecorderRef.current = recorder;
            recorder.start();
            setRecording(true);
        } catch {
            /* mic denied */
        }
    };

    const stopVoiceRecord = () => {
        mediaRecorderRef.current?.stop();
        setRecording(false);
    };

    const typingLabel = Object.keys(typingUsers)
        .filter((u) => u !== username)
        .join(", ");

    const messageGroups = groupMessagesByDate(messages);
    const isGroupChat = activeConversation?.type === "group";

    return (
        <>
            <audio id="team-chat-remote-audio" autoPlay playsInline className="team-chat-hidden-audio" />

            {showGroupModal && (
                <GroupModal
                    onClose={() => setShowGroupModal(false)}
                    onCreate={createGroup}
                    users={allUsers}
                    onlineUsers={onlineUsers}
                    getInitials={getInitials}
                />
            )}

            {incomingCall && !activeCall && (
                <div className="team-chat-call-popup">
                    <div className="team-chat-call-popup-inner">
                        <div className="team-chat-call-ring">
                            <i className="bi bi-telephone-inbound-fill"></i>
                        </div>
                        <strong>{incomingCall.from}</strong>
                        <span>{incomingCall.callType === "video" ? "Video call" : "Voice call"}</span>
                        <div className="team-chat-call-actions">
                            <button type="button" className="team-chat-call-btn team-chat-call-btn--accept" onClick={acceptCall}>
                                <i className="bi bi-telephone-fill"></i>
                            </button>
                            <button type="button" className="team-chat-call-btn team-chat-call-btn--reject" onClick={rejectCall}>
                                <i className="bi bi-telephone-x-fill"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeCall && (
                <div className="team-chat-call-bar">
                    <i className="bi bi-telephone-fill"></i>
                    <span>
                        {activeCall.outgoing ? "Calling" : "On call with"} {activeCall.with}
                    </span>
                    <button type="button" className="team-chat-call-btn team-chat-call-btn--reject" onClick={hangUp}>
                        <i className="bi bi-telephone-x-fill"></i> End
                    </button>
                </div>
            )}

            <div
                className={`team-chat-widget floating-widget-root ${open ? "team-chat-widget--open" : ""} ${minimized ? "team-chat-widget--minimized" : ""}`}
                style={rootStyle}
            >
                {open && !minimized && (
                    <div className="team-chat-panel" role="dialog" aria-label="Team Chat">
                        <div className="team-chat-panel-header">
                            <span className="floating-widget-grip" {...dragHandleProps} aria-hidden="true">
                                <i className="bi bi-grip-vertical"></i>
                            </span>
                            {view === "chat" && (
                                <button
                                    type="button"
                                    className="team-chat-icon-btn team-chat-icon-btn--light"
                                    onClick={() => setView("list")}
                                >
                                    <i className="bi bi-arrow-left"></i>
                                </button>
                            )}
                            <div className="team-chat-panel-header-main">
                                <div className="team-chat-header-avatar">
                                    <i className="bi bi-chat-dots-fill"></i>
                                </div>
                                <div>
                                    <strong>
                                        {view === "chat"
                                            ? activeConversation?.name || "Chat"
                                            : "Team Chat"}
                                    </strong>
                                    <span>
                                        {view === "chat"
                                            ? activeConversation?.type === "group"
                                                ? `${activeConversation?.members?.length || 0} members`
                                                : onlineUsers.includes(
                                                      activeConversation?.members?.find((m) => m !== username) || ""
                                                  )
                                                  ? "Online"
                                                  : "Offline"
                                            : `${onlineUsers.length} online · ${conversations.length} chats`}
                                    </span>
                                </div>
                            </div>
                            <div className="team-chat-panel-header-actions">
                                {view === "list" && (
                                    <>
                                        <button
                                            type="button"
                                            className="team-chat-icon-btn team-chat-icon-btn--light"
                                            onClick={() => setShowGroupModal(true)}
                                            title="New group"
                                        >
                                            <i className="bi bi-people-fill"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="team-chat-icon-btn team-chat-icon-btn--light"
                                            onClick={() => setView("users")}
                                            title="Team members"
                                        >
                                            <i className="bi bi-person-plus-fill"></i>
                                        </button>
                                    </>
                                )}
                                {view === "chat" && activeConversation?.type === "direct" && (
                                    <>
                                        <button
                                            type="button"
                                            className="team-chat-icon-btn team-chat-icon-btn--light"
                                            onClick={() => startCall("audio")}
                                            title="Voice call"
                                        >
                                            <i className="bi bi-telephone-fill"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="team-chat-icon-btn team-chat-icon-btn--light"
                                            onClick={() => startCall("video")}
                                            title="Video call"
                                        >
                                            <i className="bi bi-camera-video-fill"></i>
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="team-chat-icon-btn team-chat-icon-btn--light"
                                    onClick={() => setOpen(false)}
                                    title="Minimize"
                                >
                                    <i className="bi bi-dash-lg"></i>
                                </button>
                                <button
                                    type="button"
                                    className="team-chat-icon-btn team-chat-icon-btn--light"
                                    onClick={() => {
                                        setOpen(false);
                                        minimize();
                                    }}
                                    title="Minimize to icon"
                                >
                                    <i className="bi bi-arrows-angle-contract"></i>
                                </button>
                            </div>
                        </div>

                        {view === "list" && (
                            <>
                                <div className="team-chat-tabs">
                                    {["all", "direct", "groups"].map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            className={`team-chat-tab ${tab === t ? "active" : ""}`}
                                            onClick={() => setTab(t)}
                                        >
                                            {t === "all" ? "All" : t === "direct" ? "Direct" : "Groups"}
                                        </button>
                                    ))}
                                </div>
                                <div className="team-chat-conv-list">
                                    {conversations.length === 0 && (
                                        <div className="team-chat-empty">
                                            <i className="bi bi-chat-square-text"></i>
                                            <p>No conversations yet</p>
                                            <button
                                                type="button"
                                                className="team-chat-btn team-chat-btn--primary team-chat-btn--sm"
                                                onClick={() => setView("users")}
                                            >
                                                Message a teammate
                                            </button>
                                        </div>
                                    )}
                                    {conversations.map((conv) => (
                                        <button
                                            key={conv.id}
                                            type="button"
                                            className="team-chat-conv-item"
                                            onClick={() => openChat(conv)}
                                        >
                                            <div className="team-chat-avatar">
                                                {conv.type === "group" ? (
                                                    <i className="bi bi-people-fill"></i>
                                                ) : (
                                                    getInitials(conv.name)
                                                )}
                                            </div>
                                            <div className="team-chat-conv-info">
                                                <div className="team-chat-conv-top">
                                                    <strong>{conv.name}</strong>
                                                    {conv.lastMessage && (
                                                        <span className="team-chat-conv-time">
                                                            {new Date(conv.lastMessage.Created_At).toLocaleTimeString([], {
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            })}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="team-chat-conv-preview">
                                                    {conv.lastMessage?.Sender_Name === username ? "You: " : ""}
                                                    {conv.lastMessage
                                                        ? conv.lastMessage.Message_Type === "file"
                                                            ? `📎 ${conv.lastMessage.File_Name || "File"}`
                                                            : conv.lastMessage.Message_Type === "voice"
                                                              ? "🎤 Voice"
                                                              : conv.lastMessage.Body
                                                        : "Start chatting"}
                                                </div>
                                            </div>
                                            {conv.unreadCount > 0 && (
                                                <span className="team-chat-unread-badge">
                                                    {conv.unreadCount > 99 ? "99+" : conv.unreadCount}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {view === "users" && (
                            <div className="team-chat-user-list">
                                <button
                                    type="button"
                                    className="team-chat-back-link"
                                    onClick={() => setView("list")}
                                >
                                    <i className="bi bi-arrow-left"></i> Back to chats
                                </button>
                                {allUsers.map((u) => (
                                    <button
                                        key={u.Id}
                                        type="button"
                                        className="team-chat-user-item"
                                        onClick={() => startDirectChat(u.Name)}
                                    >
                                        <div className="team-chat-avatar">{getInitials(u.Name)}</div>
                                        <div>
                                            <strong>{u.Name}</strong>
                                            <span>{u.Role}</span>
                                        </div>
                                        {onlineUsers.includes(u.Name) && (
                                            <span className="team-chat-online-dot" title="Online"></span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}

                        {view === "chat" && (
                            <div className="team-chat-chat-view">
                                <div className="team-chat-messages">
                                    {loadingMessages && messages.length === 0 && (
                                        <div className="team-chat-loading">
                                            <div className="team-chat-spinner"></div>
                                        </div>
                                    )}
                                    {!loadingMessages && messages.length === 0 && (
                                        <div className="team-chat-chat-empty">
                                            <i className="bi bi-chat-heart"></i>
                                            <p>Say hello to start the conversation</p>
                                        </div>
                                    )}
                                    {messageGroups.map((item) =>
                                        item.type === "date" ? (
                                            <div key={item.key} className="team-chat-date-divider">
                                                <span>{item.label}</span>
                                            </div>
                                        ) : (
                                            <MessageBubble
                                                key={item.key}
                                                msg={item.msg}
                                                isOwn={item.msg.Sender_Name === username}
                                                parseMentions={parseMentions}
                                                getMessageStatus={getMessageStatus}
                                                showSender={isGroupChat}
                                            />
                                        )
                                    )}
                                    {typingLabel && (
                                        <div className="team-chat-typing-indicator">
                                            {typingLabel} {typingLabel.includes(",") ? "are" : "is"} typing…
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {showMentionList && mentionCandidates.length > 0 && (
                                    <div className="team-chat-mention-dropdown">
                                        {mentionCandidates.map((m) => (
                                            <button key={m} type="button" onClick={() => insertMention(m)}>
                                                @{m}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div className="team-chat-composer">
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        hidden
                                        onChange={handleFileSelect}
                                    />
                                    <button
                                        type="button"
                                        className="team-chat-composer-btn"
                                        onClick={() => fileRef.current?.click()}
                                        title="Attach file"
                                    >
                                        <i className="bi bi-paperclip"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className={`team-chat-composer-btn ${recording ? "recording" : ""}`}
                                        onMouseDown={startVoiceRecord}
                                        onMouseUp={stopVoiceRecord}
                                        onTouchStart={startVoiceRecord}
                                        onTouchEnd={stopVoiceRecord}
                                        title="Hold to record voice"
                                    >
                                        <i className="bi bi-mic-fill"></i>
                                    </button>
                                    <textarea
                                        className="team-chat-composer-input"
                                        rows={1}
                                        placeholder="Type a message… Use @ to tag someone"
                                        value={input}
                                        onChange={onInputChange}
                                        onKeyDown={handleKeyDown}
                                        disabled={sending}
                                    />
                                    <button
                                        type="button"
                                        className="team-chat-send-btn"
                                        onClick={handleSend}
                                        disabled={sending || !input.trim()}
                                    >
                                        <i className="bi bi-send-fill"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="team-chat-launcher-wrap">
                {!open && !minimized && recentNotif && (
                    <div
                        className="team-chat-notif-float"
                        role="alert"
                        onClick={() => setOpen(true)}
                    >
                        <div className="team-chat-notif-float-count">
                            {recentNotif.totalNew > 99 ? "99+" : recentNotif.totalNew}
                        </div>
                        <div className="team-chat-notif-float-body">
                            <strong>
                                {recentNotif.totalNew > 1
                                    ? `${recentNotif.totalNew} new messages`
                                    : "New message"}
                            </strong>
                            <span>
                                {recentNotif.totalUnread} unread total
                                {recentNotif.batches?.length > 1
                                    ? ` · ${recentNotif.batches.length} chats`
                                    : recentNotif.batches?.[0]?.convName
                                      ? ` · ${recentNotif.batches[0].convName}`
                                      : ""}
                            </span>
                            {recentNotif.batches?.[0] && (
                                <small>
                                    {recentNotif.batches[0].from}:{" "}
                                    {formatPreview(recentNotif.batches[0].lastMessage)}
                                </small>
                            )}
                        </div>
                    </div>
                )}

                <span className="floating-widget-grip floating-widget-grip--launcher" {...dragHandleProps} aria-hidden="true">
                    <i className="bi bi-grip-vertical"></i>
                </span>

                <button
                    type="button"
                    className={`team-chat-launcher ${minimized ? "team-chat-launcher--mini" : ""}`}
                    onClick={() => {
                        if (minimized) restore();
                        setOpen((v) => !v);
                    }}
                    aria-label={open ? "Close team chat" : "Open team chat"}
                    title={totalUnread > 0 ? `${totalUnread} unread chat message${totalUnread !== 1 ? "s" : ""}` : "Team Chat"}
                >
                    {open && !minimized ? (
                        <i className="bi bi-chevron-down"></i>
                    ) : (
                        <>
                            <i className="bi bi-chat-dots-fill"></i>
                            {!minimized && (
                                <span className="team-chat-launcher-label">Team Chat</span>
                            )}
                            {totalUnread > 0 && (
                                <span className="team-chat-launcher-badge">
                                    {totalUnread > 99 ? "99+" : totalUnread}
                                </span>
                            )}
                        </>
                    )}
                </button>
                </div>
            </div>
        </>
    );
}
